import { SALON_DETAILS_SUCCESS } from 'actions/fetchSalon.types';
import fePickCurrentTime from 'actions/scheduler/fePickCurrentTime.action';
import beDeleteTimeOffCategory from 'actions/timeoff/beDeleteTimeOffCategory.action';
import beSaveTimeOffAllowance from 'actions/timeoff/beSaveTimeOffAllowance.action';
import beSaveTimeOffCategory from 'actions/timeoff/beSaveTimeOffCategory.action';
import beTimeOffAllowances from 'actions/timeoff/beTimeOffAllowances.action';
import beTimeOffCategories from 'actions/timeoff/beTimeOffCategories.action';
import beTimeOffReport from 'actions/timeoff/beTimeOffReport.action';
import feDeleteLocalTimeOffCategory from 'actions/timeoff/feDeleteLocalTimeOffCategory.action';
import feUpdateTimeOffCategories from 'actions/timeoff/feUpdateTimeOffCategories.action';
import feUpdateTimeOffData from 'actions/timeoff/feUpdateTimeOffData.action';
import { feSidebarOpen } from 'actions/ui.actions';
import determineDefaultTimeOffCategory from 'bl/timeoff/determineDefaultTimeOffCategory.bl';
import fromTimeOffCategoryFormToData from 'bl/timeoff/fromTimeOffCategoryFormToData.bl';
import fromTimeOffFormToData from 'bl/timeoff/fromTimeOffFormToData.bl';
import RRS from 'constants/recurrence/recurrenceRuleSimplified.const';
import TIME_OFF_DURATIONS from 'constants/timeoff/durations.const';
import TIME_OFF_REPEAT from 'constants/timeoff/repeat.const';
import SP from 'constants/ui/sidePanel.const';
import fromDtosToCategories from 'dto/timeOff/fromDtosToCategories.dto';
import fromDtoToCategory from 'dto/timeOff/fromDtoToCategory.dto';
import fromDtoToTimeOffReport from 'dto/timeOff/fromDtoToTimeOffReport.dto';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import removeById from 'utils/arrays/removeById.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromBeToBody from 'utils/converters/backend/fromBeToBody.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromActionToFtag from 'utils/converters/log/fromActionToFtag.util';
import { fromAnyDateToIso, fromAnyDateToIsoDayBgn, fromAnyDateToTimestamp, fromAnyDateToZuluDayBgn } from 'utils/converters/time/from.util';
import { formatHourAndMinute } from 'utils/date/format.util';
import { timeAdd60min } from 'utils/date/manipulate.util';
import { nowAsIso } from 'utils/date/now.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import C from 'utils/values/constant.util';
import randomString from 'utils/values/randomString.util';

import { CLEAN_NEWBOOKING_DATA } from 'booking/constants';

const L = logger('timeOffReducer');

const determineReferenceDate = ({ data, state, init }) => {
  const result1 = fromAnyDateToIso(getAnyOrNull('block.day', data));
  if (result1) return result1;

  const result2 = fromAnyDateToIso(getAnyOrNull('form.referenceDate', state));
  if (result2) return result2;

  if (!init) return '';

  const result3 = fromAnyDateToIso(getAnyOrNull('picked', state));
  if (result3) return result3;

  return nowAsIso();
};

const DEFAULT = C({
  allowances: [],
  categories: [],
  data: {},
  form: {
    block: {
      day: '',
      from: { value: '', description: '' }, // TODO: should only be the value, no description, no object, just string
      to: { value: '', description: '' }, // TODO: should only be the value, no description, no object, just string
    },
  },
  init: null,
  meta: {},
  nonce: '',
  picked: '',
  report: {},
});

const MAP = Object.freeze({
  [CLEAN_NEWBOOKING_DATA]: (state) => ({ ...state, data: {}, form: {}, init: null, meta: {}, nonce: '', picked: '' }),

  [fePickCurrentTime.AT.name]: (state, action) => {
    const FTAG = fromActionToFtag(action);

    const { form } = asObject(state);
    const { data } = asObject(action);
    const { time } = asObject(data);

    const day = fromAnyDateToZuluDayBgn(time);
    const picked = fromAnyDateToIso(time);

    L.debug(FTAG, 'picked:', picked);

    return { ...state, picked, form: { ...form, block: { ...form.block, day } } };
  },

  [feSidebarOpen.AT.name]: (state, action) => {
    const FTAG = fromActionToFtag(action);

    const data = asObject(action.data);
    const event = asObject(data.event);

    const eventSpan = fromAnyDateToTimestamp(event.end_time) - fromAnyDateToTimestamp(event.start_time);
    const isAllDay = eventSpan / 3600000 > 19.75; // FIXME: find better way to determine full day than this hacky one

    const bgnIso = fromAnyDateToIso(data.time);
    const endIso = fromAnyDateToIso(timeAdd60min(data.time));
    const dayIso = fromAnyDateToIsoDayBgn(data.time);

    const form = {
      ...state.form,
      repeat: TIME_OFF_REPEAT[RRS.onlyOnce],
      duration: TIME_OFF_DURATIONS.block,
      repeatUntil: null,
      referenceDate: determineReferenceDate({ data, state, init: true }),
      fullDay: dayIso,
      block: {
        day: dayIso,
        from: { value: bgnIso, description: formatHourAndMinute(bgnIso) },
        to: { value: endIso, description: formatHourAndMinute(endIso) },
      },
      category: data.category || event.time_off_category,
      note: fromAnyToTrimmed(data.note) || fromAnyToTrimmed(event.notes),
    };

    const createData = SP.timeOff === data.panel ? fromTimeOffFormToData({ ...form, ...data }) : void 1;
    const editCatData = SP.timeOffCat === data.panel ? fromTimeOffCategoryFormToData({ ...form, ...data }) : void 1;

    const result = {
      ...state,
      init: data,
      meta: { eventSpan, isAllDay },
      nonce: state.nonce || randomString.long(),
      form,
      data: { ...state.data, ...editCatData, ...createData, salonId: data.salonId, stylistId: data.stylistId },
    };

    L.debug(FTAG, data.panel, result, '<-', { event, data, form });
    return result;
  },

  [SALON_DETAILS_SUCCESS]: (state, data) => {
    const { pricing_plan: pricingPlanName, pricing_plan_id: pricingPlanId } = fromBeToBody(data);
    return { ...state, pricingPlanName, pricingPlanId };
  },

  [beTimeOffCategories.AT.pass]: (state, data) => {
    const { form, pricingPlanName, pricingPlanId } = state;

    const categories = fromDtosToCategories(asObject(data).json);
    const defaultCategory = determineDefaultTimeOffCategory({ categories, pricingPlanName, pricingPlanId });

    return {
      ...state,
      categories,
      form: { ...form, category: defaultCategory || asObject(form).category },
    };
  },

  [beSaveTimeOffCategory.AT.pass]: (state, data) => {
    const { categories: categoriesFromState } = state;
    const { params, response } = asObject(data);
    const { id, isNew } = asObject(params);

    const categories = isNew ? [...removeById(id, categoriesFromState), fromDtoToCategory(response)] : categoriesFromState.map((c) => (c.id === id ? fromDtoToCategory(response) : c));

    return { ...state, categories };
  },

  [beDeleteTimeOffCategory.AT.pass]: (state, { params }) => ({
    ...state,
    categories: removeById(fromAnyToInteger(params.id), state.categories),
  }),

  [feUpdateTimeOffCategories.AT.name]: (state, { data }) => ({
    ...state,
    categories: [...state.categories, asObject(data)],
  }),

  [feDeleteLocalTimeOffCategory.AT.name]: (state, { data }) => ({
    ...state,
    categories: removeById(asObject(data).id, state.categories),
  }),

  [feUpdateTimeOffData.AT.name]: (state, action) => {
    const FTAG = fromActionToFtag(action);

    const panel = state?.init?.panel;
    const data = action?.data ?? {};

    const form = { ...state.form, ...data, referenceDate: determineReferenceDate({ data, state }) };

    const createData = SP.timeOff === panel ? fromTimeOffFormToData(form) : void 1;
    const editCatData = SP.timeOffCat === panel ? fromTimeOffCategoryFormToData(form) : void 1;

    const result = { ...state, form, data: { ...state.data, ...editCatData, ...createData } };

    L.debug(FTAG, panel, result, '<-', { data, form });
    return result;
  },

  // Time Off Report
  // TODO: remap report on intake
  [beTimeOffReport.AT.pass]: (state, data) => {
    const dto = asObject(data).json; // Data Transfer Object
    const pvo = fromDtoToTimeOffReport(dto); // Plain Value Object
    return { ...state, report: { dto, pvo } };
  },

  // TODO: remap allowances on intake
  [beTimeOffAllowances.AT.pass]: (state, data) => ({ ...state, allowances: asObject(data).response }),

  [beSaveTimeOffAllowance.AT.pass]: (state, data) => {
    const { allowances: allowancesFromState } = asObject(state);
    const { params, response } = asObject(data);
    const { id, isNew } = asObject(params);

    const allowances = isNew ? [...allowancesFromState, response] : [...removeById(id, allowancesFromState), response];

    return { ...state, allowances };
  },
});

// this top level reducer serves to set up defaults for the state, if needed, as deep as necessary
// determine the action data from variety of different action shapes
// and delegate to the specific handlers per the action type
const timeOffReducer = (state, action) => (MAP[action && action.type] || alwaysSame)(state || { ...DEFAULT }, asObject(action));

export default timeOffReducer;
